/** PRO-02 Catalogue tarifé · PRO-03 Fiche produit (palette par défaut) · PRO-04 Commande rapide. */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Icon, PackSelector, Pagination, QuantityInput, SearchField, SelectField, StockIndicator, Table, TextField, useToast, type EtatStock } from '@myron/design-system';
import { Package, Plus, ShoppingCart, Trash2 } from 'lucide-react';
import type { Page, S } from '@myron/contracts';
import { get, messageErreur, post } from '../../shared/api/client';
import { SiPermission, useSession } from '../../shared/auth/session';
import { EntetePage, ErreurFormulaire, EtatRequete } from '../../shared/ui/composants';
import { euros, pourcentBp } from '../../shared/ui/format';
import { cles, detailConditionnement, LIBELLE_SOURCE, useCatalogue, useEntrepots, useMutationsPanier, type Offre, type ProduitPro, type TypeCond } from './api';

const LIB_COND: Record<string, string> = { PALETTE: 'Palette', CARTON: 'Carton', PACK: 'Pack' };

function meilleureDispo(p: ProduitPro): EtatStock {
  const etats = (p.disponibilite ?? []).map((d) => d.etat as EtatStock);
  for (const e of ['DISPONIBLE', 'FAIBLE', 'SUR_COMMANDE'] as EtatStock[]) if (etats.includes(e)) return e;
  return 'RUPTURE';
}
const offreDefaut = (p: ProduitPro) => p.offres?.find((o) => o.conditionnement?.parDefaut) ?? p.offres?.[0];

function Prix({ o }: { o: Offre | undefined }) {
  if (!o) return <span className="my-secondaire">Prix sur demande</span>;
  return (
    <span className="my-pile my-pile--serre" style={{ gap: 2 }}>
      <span className="my-prix">{euros(o.prixUnitaireHtCentimes)} <span className="my-legende my-secondaire">HT / {LIB_COND[o.conditionnement!.type!]?.toLowerCase()}</span></span>
      {o.prixBarreCentimes ? <span className="my-prix-barre my-petit">{euros(o.prixBarreCentimes)}</span> : null}
      <span className="my-legende my-secondaire">{LIBELLE_SOURCE[o.source!]}</span>
    </span>
  );
}

function BoutonAjoutRapide({ p }: { p: ProduitPro }) {
  const { ajouter } = useMutationsPanier();
  const toast = useToast();
  const o = offreDefaut(p);
  if (!o) return null;
  const c = o.conditionnement!;
  return (
    <Button
      compact
      variante="secondaire"
      icone={Plus}
      chargement={ajouter.isPending}
      aria-label={`Ajouter 1 ${LIB_COND[c.type!]?.toLowerCase()} de ${p.libelle} au panier`}
      onClick={() => ajouter.mutate({ produitId: p.id!, conditionnementType: c.type as TypeCond, quantite: Math.max(1, c.minimum ?? 1) }, {
        onSuccess: () => toast({ ton: 'succes', titre: 'Ajouté au panier', texte: `${p.libelle} — ${LIB_COND[c.type!]}` }),
        onError: (e) => toast({ ton: 'erreur', titre: 'Ajout impossible', texte: messageErreur(e) }),
      })}
    >
      1 {LIB_COND[c.type!]?.toLowerCase()}
    </Button>
  );
}

export function CatalogueTarife() {
  const [params, setParams] = useSearchParams();
  const [saisie, setSaisie] = useState(params.get('q') ?? '');
  const entrepots = useEntrepots();
  const categories = useQuery({ queryKey: ['public', 'categories'], queryFn: () => get<S<'Categorie'>[]>('/catalogue/categories'), staleTime: 300_000 });
  const filtres = { q: params.get('q') ?? undefined, categorieId: params.get('categorieId') ?? undefined, conditionnement: params.get('conditionnement') ?? undefined, entrepotId: params.get('entrepotId') ?? undefined, page: Number(params.get('page') ?? 1), taille: 20 };
  const q = useCatalogue(filtres);
  const changer = (cle: string, v?: string) => {
    const n = new URLSearchParams(params);
    if (v) n.set(cle, v); else n.delete(cle);
    if (cle !== 'page') n.delete('page');
    setParams(n);
  };
  return (
    <>
      <EntetePage titre="Catalogue" sousTitre="Vos prix personnalisés, hors taxes. La palette est le conditionnement prioritaire." />
      <form className="my-form-ligne" style={{ marginBottom: 24, gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }} role="search" onSubmit={(e) => { e.preventDefault(); changer('q', saisie.trim() || undefined); }}>
        <div className="my-pile my-pile--serre">
          <SearchField label="Rechercher un produit" placeholder="Produit, référence, EAN…" value={saisie} onChange={(e) => setSaisie(e.target.value)} />
          <Button type="submit" variante="secondaire" compact>Rechercher</Button>
        </div>
        <SelectField label="Rayon" value={filtres.categorieId ?? ''} onChange={(e) => changer('categorieId', e.target.value || undefined)} options={[{ value: '', label: 'Tous les rayons' }, ...(categories.data ?? []).map((c) => ({ value: c.id!, label: c.libelle! }))]} />
        <SelectField label="Conditionnement" value={filtres.conditionnement ?? ''} onChange={(e) => changer('conditionnement', e.target.value || undefined)} options={[{ value: '', label: 'Tous' }, { value: 'PALETTE', label: 'Palette' }, { value: 'CARTON', label: 'Carton' }]} />
        <SelectField label="Disponibilité dans" value={filtres.entrepotId ?? ''} onChange={(e) => changer('entrepotId', e.target.value || undefined)} options={[{ value: '', label: 'Tous les entrepôts' }, ...(entrepots.data ?? []).map((x) => ({ value: x.id!, label: x.nom! }))]} />
      </form>
      <EtatRequete
        requete={q}
        vide={(d) => d.donnees.length === 0}
        titreVide="Aucun produit ne correspond"
        actionVide={<Button variante="secondaire" onClick={() => { setSaisie(''); setParams(new URLSearchParams()); }}>Effacer les filtres</Button>}
        enfants={(d: Page<ProduitPro>) => (
          <div className="my-pile">
            <p className="my-petit my-secondaire" aria-live="polite">{d.total} produit(s)</p>
            <Table<ProduitPro>
              legende="Catalogue tarifé"
              lignes={d.donnees}
              cle={(p) => p.id!}
              colonnes={[
                { cle: 'produit', titre: 'Produit', rendu: (p) => <span className="my-pile my-pile--serre" style={{ gap: 2 }}><Link to={`/pro/catalogue/${p.id}`}><strong>{p.libelle}</strong></Link><span className="my-legende my-secondaire">{p.marque} · Réf. {p.sku}</span></span> },
                { cle: 'prix', titre: 'Votre prix', rendu: (p) => <Prix o={offreDefaut(p)} /> },
                { cle: 'cond', titre: 'Conditionnements', rendu: (p) => <span className="my-ligne">{(p.offres ?? []).map((o) => <Badge key={o.conditionnement!.type} ton={o.conditionnement!.type === 'PALETTE' ? 'marque' : 'neutre'}>{LIB_COND[o.conditionnement!.type!]}</Badge>)}</span> },
                { cle: 'dispo', titre: 'Disponibilité', rendu: (p) => <StockIndicator etat={meilleureDispo(p)} /> },
                { cle: 'actions', titre: <span className="my-sr">Actions</span>, actions: true, rendu: (p) => <span className="my-ligne" style={{ justifyContent: 'flex-end' }}><SiPermission permission="panier.gerer"><BoutonAjoutRapide p={p} /></SiPermission><Link className="my-btn my-btn--tertiaire my-btn--compact" to={`/pro/catalogue/${p.id}`}>Détails</Link></span> },
              ]}
            />
            <Pagination page={d.page} taille={d.taille} total={d.total} onPage={(p) => changer('page', String(p))} />
          </div>
        )}
      />
    </>
  );
}

export function FicheProduitPro() {
  const { produitId = '' } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const { peut } = useSession();
  const q = useQuery({ queryKey: cles.produit(produitId), queryFn: () => get<ProduitPro>(`/pro/catalogue/produits/${produitId}`) });
  const entrepots = useEntrepots();
  const { ajouter } = useMutationsPanier();
  const [type, setType] = useState<TypeCond | ''>('');
  const [quantite, setQuantite] = useState(1);
  const p = q.data;
  const offre = useMemo(() => p?.offres?.find((o) => o.conditionnement!.type === type), [p, type]);
  useEffect(() => {
    if (p && !type) {
      const d = offreDefaut(p);
      if (d) { setType(d.conditionnement!.type as TypeCond); setQuantite(Math.max(1, d.conditionnement!.minimum ?? 1)); }
    }
  }, [p, type]);
  const calcul = useQuery({
    queryKey: ['pro', 'prix', produitId, type, quantite],
    queryFn: () => post<S<'CalculPrixReponse'>>('/pro/prix/calculer', { lignes: [{ produitId, conditionnementType: type, quantite }] }),
    enabled: !!type && quantite > 0,
    placeholderData: (x) => x,
  });
  const ligne = calcul.data?.lignes?.[0];
  return (
    <EtatRequete
      requete={q}
      enfants={(p) => {
        const c = offre?.conditionnement;
        return (
          <>
            <EntetePage titre={p.libelle!} sousTitre={`${p.marque} · ${p.contenance} · Réf. ${p.sku}`} fil={[{ libelle: 'Catalogue', vers: '/pro/catalogue' }, { libelle: p.libelle! }]} />
            <div className="my-grille-2">
              <div className="my-pile">
                <div className="my-visuel" aria-hidden><Icon icon={Package} /></div>
                {p.description && <p>{p.description}</p>}
                <dl className="my-dl">
                  <dt>Rayon</dt><dd>{p.categorie}</dd>
                  <dt>TVA</dt><dd>{pourcentBp(p.tvaBp)}</dd>
                  {p.origine && <><dt>Origine</dt><dd>{p.origine}</dd></>}
                  {p.conservation && <><dt>Conservation</dt><dd>{p.conservation}</dd></>}
                  {p.allergenes && <><dt>Allergènes</dt><dd>{p.allergenes}</dd></>}
                </dl>
                <section className="my-pile my-pile--serre" aria-labelledby="titre-dispo">
                  <h2 id="titre-dispo" className="my-titre-4">Disponibilité par entrepôt</h2>
                  <ul className="my-pile my-pile--serre" style={{ listStyle: 'none', padding: 0 }}>
                    {(p.disponibilite ?? []).map((d) => (
                      <li key={d.entrepotId} className="my-ligne my-ligne--espace"><span>{entrepots.data?.find((x) => x.id === d.entrepotId)?.nom ?? 'Entrepôt'}</span><StockIndicator etat={d.etat as EtatStock} /></li>
                    ))}
                  </ul>
                  <p className="my-legende my-secondaire">Le stock est réservé uniquement après confirmation de votre paiement.</p>
                </section>
              </div>
              <Card as="section" aria-labelledby="titre-achat">
                <div className="my-pile">
                  <h2 id="titre-achat" className="my-titre-4">Votre prix</h2>
                  {!p.offres?.length ? <Alert ton="alerte">Aucun prix disponible pour ce produit. Contactez votre commercial.</Alert> : (
                    <>
                      <PackSelector
                        legende="Conditionnement"
                        valeur={type}
                        onChange={(v) => { setType(v as TypeCond); const o = p.offres!.find((x) => x.conditionnement!.type === v); setQuantite(Math.max(1, o?.conditionnement?.minimum ?? 1)); }}
                        options={p.offres.map((o) => ({ valeur: o.conditionnement!.type!, nom: `${LIB_COND[o.conditionnement!.type!]} · ${euros(o.prixUnitaireHtCentimes)}`, detail: detailConditionnement(o.conditionnement as never, p.uniteBase) }))}
                      />
                      {c && (
                        <>
                          <QuantityInput label={`Quantité (${LIB_COND[c.type!]?.toLowerCase()}s)`} valeur={quantite} onChange={setQuantite} min={c.minimum ?? 1} pas={c.multiple ?? 1} />
                          {(c.minimum ?? 1) > 1 && <p className="my-legende my-secondaire">Minimum : {c.minimum} · par multiple de {c.multiple}</p>}
                          <div className="my-pile my-pile--serre" aria-live="polite" data-testid="calcul-prix">
                            {ligne ? (
                              <>
                                <span className="my-prix">{euros(ligne.totalHtCentimes)} HT</span>
                                <span className="my-petit my-secondaire">{euros(ligne.prixUnitaireHtCentimes)} HT / {LIB_COND[c.type!]?.toLowerCase()} · {LIBELLE_SOURCE[ligne.source!]}{ligne.remiseBp ? ` · remise volume ${pourcentBp(ligne.remiseBp)}` : ''}</span>
                                {ligne.prixBarreCentimes ? <span className="my-prix-barre my-petit">au lieu de {euros(ligne.prixBarreCentimes)} / unité de vente</span> : null}
                              </>
                            ) : <span className="my-secondaire">Calcul du prix…</span>}
                          </div>
                          {!!offre?.paliers?.length && (
                            <div className="my-pile my-pile--serre">
                              <strong className="my-petit">Remises de volume</strong>
                              <ul className="my-petit" style={{ margin: 0, paddingLeft: 18 }}>
                                {offre.paliers.map((x) => <li key={x.seuilQuantite}>À partir de {x.seuilQuantite} {LIB_COND[c.type!]?.toLowerCase()}s : −{pourcentBp(x.remiseBp)}</li>)}
                              </ul>
                            </div>
                          )}
                          {peut('panier.gerer') ? (
                            <div className="my-ligne">
                              <Button icone={ShoppingCart} chargement={ajouter.isPending} onClick={() => ajouter.mutate({ produitId, conditionnementType: type as TypeCond, quantite }, {
                                onSuccess: () => toast({ ton: 'succes', titre: 'Ajouté au panier', texte: `${quantite} × ${LIB_COND[type]} — ${p.libelle}` }),
                              })}>Ajouter au panier</Button>
                              <Button variante="tertiaire" onClick={() => nav('/pro/panier')}>Voir le panier</Button>
                            </div>
                          ) : <Alert ton="info">Votre rôle permet de consulter les prix sans passer commande.</Alert>}
                          <ErreurFormulaire erreur={ajouter.error} />
                        </>
                      )}
                    </>
                  )}
                </div>
              </Card>
            </div>
          </>
        );
      }}
    />
  );
}

interface LigneRapide { cle: number; sku: string; type: TypeCond; quantite: number }

export function CommandeRapide() {
  const [lignes, setLignes] = useState<LigneRapide[]>([{ cle: 1, sku: '', type: 'PALETTE', quantite: 1 }]);
  const [rapport, setRapport] = useState<{ ok: string[]; ko: string[] } | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const { ajouter } = useMutationsPanier();
  const nav = useNavigate();
  const maj = (cle: number, champ: Partial<LigneRapide>) => setLignes((l) => l.map((x) => (x.cle === cle ? { ...x, ...champ } : x)));
  const valider = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnvoi(true);
    const ok: string[] = [];
    const ko: string[] = [];
    for (const l of lignes.filter((x) => x.sku.trim())) {
      const sku = l.sku.trim().toUpperCase();
      try {
        const r = await get<Page<ProduitPro>>('/pro/catalogue/produits', { q: sku, taille: 20 });
        const p = r.donnees.find((x) => x.sku === sku);
        if (!p) { ko.push(`${sku} : référence inconnue`); continue; }
        await ajouter.mutateAsync({ produitId: p.id!, conditionnementType: l.type, quantite: l.quantite });
        ok.push(`${sku} : ${l.quantite} × ${LIB_COND[l.type]}`);
      } catch (er) {
        ko.push(`${sku} : ${messageErreur(er)}`);
      }
    }
    setRapport({ ok, ko });
    if (!ko.length) setLignes([{ cle: Date.now(), sku: '', type: 'PALETTE', quantite: 1 }]);
    setEnvoi(false);
  };
  return (
    <>
      <EntetePage titre="Commande rapide" sousTitre="Saisissez vos références : elles sont ajoutées à votre panier avec vos prix." />
      <Card as="section">
        <form className="my-form" onSubmit={valider}>
          {lignes.map((l, i) => (
            <div key={l.cle} className="my-form-ligne" style={{ gridTemplateColumns: 'minmax(160px,2fr) minmax(130px,1fr) auto auto', alignItems: 'end' }}>
              <TextField label={`Référence ${i + 1}`} value={l.sku} onChange={(e) => maj(l.cle, { sku: e.target.value })} placeholder="ex. EAU-PLATE-50" autoComplete="off" />
              <SelectField label="Conditionnement" value={l.type} onChange={(e) => maj(l.cle, { type: e.target.value as TypeCond })} options={[{ value: 'PALETTE', label: 'Palette' }, { value: 'CARTON', label: 'Carton' }]} />
              <QuantityInput label={`Quantité ligne ${i + 1}`} valeur={l.quantite} onChange={(v) => maj(l.cle, { quantite: v })} />
              <Button variante="tertiaire" compact icone={Trash2} disabled={lignes.length === 1} onClick={() => setLignes((x) => x.filter((y) => y.cle !== l.cle))} aria-label={`Supprimer la ligne ${i + 1}`}>Retirer</Button>
            </div>
          ))}
          <div className="my-ligne">
            <Button variante="secondaire" icone={Plus} onClick={() => setLignes((x) => [...x, { cle: Date.now(), sku: '', type: 'PALETTE', quantite: 1 }])}>Ajouter une ligne</Button>
            <Button type="submit" chargement={envoi} disabled={!lignes.some((x) => x.sku.trim())}>Ajouter au panier</Button>
          </div>
          {rapport && (
            <div className="my-pile my-pile--serre" aria-live="polite">
              {rapport.ok.length > 0 && <Alert ton="succes" titre={`${rapport.ok.length} ligne(s) ajoutée(s)`} action={<Button compact variante="secondaire" onClick={() => nav('/pro/panier')}>Voir le panier</Button>}>{rapport.ok.join(' · ')}</Alert>}
              {rapport.ko.length > 0 && <Alert ton="erreur" titre="Lignes non ajoutées">{rapport.ko.join(' · ')}</Alert>}
            </div>
          )}
        </form>
      </Card>
    </>
  );
}
